import {ApplicationRef, ChangeDetectorRef, Component, Input, OnInit, OnChanges} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {TranslocoService} from "@ngneat/transloco";
import {
    ExperienceLevelType,
    ICRUDRecord,
    IPlayerProps
} from "../../../../interfaces";
import {actionCRUDRecord, actionUpdateSceneItem} from "../../../../store/actions/user.actions";
import {CommonService, ContainerService, LocalStoreService, StoreSelectorsService} from "../../../../services";
import {Compbaser} from "../../../../components/compbaser";
import {DialogCategoryTreeComponent} from '../../../../components/dialog-category-tree/dialog-category-tree.component';
import {isMainTouchScene, preprocessSceneRecord} from 'app/common';
import {debounce} from "utils-decorators";
import { TouchLib } from '../touchLib';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import * as _ from 'lodash';
import { DialogSwitchConfirmComponent, SwitchConfirmDialogData } from '../dialog-switch-confirm/dialog-switch-confirm.component';

@Component({
    selector: 'app-touch-scene-props',
    templateUrl: './touch-scene-props.component.html',
    styleUrls: ['./touch-scene-props.component.scss']
})
export class TouchScenePropsComponent extends Compbaser implements OnInit, OnChanges {
    category = '';
    categories: Array<string> = [];
    sceneWidth: number;
    sceneHeight: number;

    readonly experienceLevelType = ExperienceLevelType;
    experienceLevel: ExperienceLevelType;

    // Slider-specific properties
    durationControl = new FormControl();
    navigationMode: 'slide' | 'time' = 'slide';
    navigationOptions = [
        { value: 'slide', label: 'slide' },
        { value: 'time', label: 'time' }
    ];

    @Input() renderScene: IPlayerProps;

    constructor(private storeSelector: StoreSelectorsService,
                private translocoService: TranslocoService,
                private containerService: ContainerService,
                private localStoreService: LocalStoreService,
                private commonService: CommonService,
                public override dialog: MatDialog,
                private cdr: ChangeDetectorRef,
                private appRef: ApplicationRef) {
        super();
        this.me = 'scene-list-props';
        this.cod(
            this.storeSelector.getLocalStoreUpdates('ILocalStorageConfig').subscribe((e) => {
                this.experienceLevel = this.localStoreService.getStorageValConfig('experienceLevel');
            })
        );
    }

    ngOnInit() {
        this.cod(
            this.containerService.getCategories().subscribe((i_categories) => {
                this.categories = i_categories ? i_categories : [];
            })
        );

        // Setup reactive duration control for sliders
        this.cod(
            this.durationControl.valueChanges.pipe(
                debounceTime(500),
                distinctUntilChanged(),
                filter(value => value !== null && value !== undefined && !isNaN(value)),
            ).subscribe(duration => {
                this.saveDuration(duration);
            }),
        );

        // Initialize component with renderScene data
        if (this.renderScene) {
            this.initializeComponent();
        }
    }

    ngOnChanges() {
        if (this.renderScene) {
            this.initializeComponent();
        }
    }

    private initializeComponent() {
        this.category = this.renderScene.Player?.$?.folder;
        this.sceneWidth = parseInt(this.renderScene?.Player?.Data[0]?.Layout[0]?.$.width);
        this.sceneHeight = parseInt(this.renderScene?.Player?.Data[0]?.Layout[0]?.$.height);

        if (isMainTouchScene(this.renderScene)) {
            this.navigationMode = TouchLib.isCollection(this.renderScene) ? 'slide' : 'time';
            this.durationControl.setValue(this.getDuration(this.renderScene), { emitEvent: false });
        }
    }

    @debounce(333)
    onChangeSceneName(event) {
        const sceneName = event.target.value;
        if (!sceneName || !this.renderScene) return;

        this.storeSelector.dispatch(actionUpdateSceneItem({
            sceneId: this.renderScene.Player.$.id,
            sceneItemId: this.renderScene.Player.$.id,
            values: [{
                key: 'label',
                value: sceneName,
                storePath: '$'
            }],
            disableCRUD: true
        }));
        setTimeout(() => {
            this.updateScene(this.renderScene.Player.$.id, this.renderScene);
        });

        // update scheduler event value with new scene name
        this.commonService.updateScheduleEventValue('scene', [this.renderScene.Player.$.id], sceneName);
    }

    updateTreePath(filterValue: string) {
        if (!this.renderScene) return;

        const sceneId = this.renderScene.Player.$.id;
        this.storeSelector.dispatch(actionUpdateSceneItem({
            sceneId,
            sceneItemId: sceneId,
            values: [{
                key: 'folder',
                value: filterValue,
                storePath: '$'
            }],
            disableCRUD: true
        }));

        const saveRecord: ICRUDRecord = {
            studioId: this.localStoreService.getStorageConfig().studioId,
            command: 'update',
            primary: 'player_data_id',
            table: 'player_data',
            body: {
                tree_path: filterValue
            },
            id: sceneId
        };
        this.storeSelector.dispatch(actionCRUDRecord({saveRecord}));
    }

    openCategoryTree() {
        this.dialog.open(DialogCategoryTreeComponent, {
            width: '500px',
            data: {
                categories: this.categories,
                treePath: this.category,
                callback: (treePath: string) => {
                    this.updateTreePath(treePath);
                }
            }
        });
    }

    private updateScene(sceneId, scene) {
        const saveRecord: ICRUDRecord = preprocessSceneRecord(scene, sceneId, this.localStoreService.getStorageConfig().studioId, 'update');
        this.storeSelector.dispatch(actionCRUDRecord({saveRecord}));
    }

    getDuration(row): number {
        return parseInt(TouchLib.getDuration(row)) || 5;
    }

    getPagesLength(row): number {
        return TouchLib.getPagesLength(row);
    }

    private saveDuration(duration: number) {
        if (!this.renderScene) return;

        const validDuration = Math.max(1, duration || 5);

        this.storeSelector.dispatch(actionUpdateSceneItem({
            sceneId: this.renderScene.Player.$.id,
            sceneItemId: this.renderScene.Player.$.id,
            values: [{
                key: "duration",
                value: validDuration.toString(),
                storePath: "Data[0].Scene[0].Players[0].Player[0].Data[0].Playlist[0].$",
            }],
            disableCRUD: true,
        }));
        setTimeout(() => {
            this.updateScene(this.renderScene.Player.$.id, this.renderScene);
        });
    }

    onChangeNavigationMode(selectedValue?: string) {
        if (!this.renderScene) return;

        const newMode = selectedValue as 'slide' | 'time';
        if (newMode && newMode !== this.navigationMode) {
            // Store the original mode to revert to if cancelled
            const originalMode = this.navigationMode;

            // Show confirmation dialog before switching
            const currentComponent = TouchLib.getComponentName(this.renderScene);
            const targetComponent: 'Collection' | 'Playlist' = newMode === 'slide' ? 'Collection' : 'Playlist';

            const dialogData: SwitchConfirmDialogData = {
                fromComponent: currentComponent,
                toComponent: targetComponent
            };

            const dialogRef = this.dialog.open(DialogSwitchConfirmComponent, {
                width: '500px',
                data: dialogData,
                disableClose: true
            });

            this.cod(
                dialogRef.afterClosed().subscribe(result => {
                    if (result) {
                        // User confirmed, proceed with the switch
                        this.performComponentSwitch(newMode);
                    } else {
                        // User cancelled, revert the UI selection to original mode
                        // Force a more explicit update by temporarily changing to null then back
                        this.navigationMode = null as any;
                        this.cdr.detectChanges();
                        setTimeout(() => {
                            this.navigationMode = originalMode;
                            this.cdr.detectChanges();
                        }, 0);
                    }
                })
            );
        } else if (!selectedValue) {
            // Direct call without selectedValue (existing behavior)
            this.performComponentSwitch(this.navigationMode);
        }
    }

    private performComponentSwitch(newMode: 'slide' | 'time') {
        if (!this.renderScene) return;

        this.navigationMode = newMode;

        const prevComponentName = this.navigationMode === 'slide' ? 'Playlist': 'Collection';
        const newComponentName = this.navigationMode === 'slide' ? 'Collection': 'Playlist';
        const prevBlock = this.renderScene.Player.Data[0].Scene[0].Players[0].Player[0];
        const prevPages = prevBlock.Data[0][prevComponentName][0].Page;
        const prevLayout = prevBlock.Data[0].Layout;

        let blockData;

        if (this.navigationMode === 'time') {
            blockData = _.cloneDeep(this.storeSelector.blockLabels.PLAYLIST.template);
        } else {
            blockData = _.cloneDeep(this.storeSelector.blockLabels.COLLECTION.template);
        }

        blockData.Data[0].Layout = prevLayout;
        blockData.$.id = prevBlock.$.id;
        blockData.Data[0][newComponentName][0].Page = prevPages;

        this.storeSelector.dispatch(actionUpdateSceneItem({
            sceneId: this.renderScene.Player.$.id,
            sceneItemId: this.renderScene.Player.$.id,
            values: [{
                key: "Player",
                value: [blockData],
                storePath: "Data[0].Scene[0].Players[0]",
            }],
            disableCRUD: true,
        }));
        setTimeout(() => {
            this.updateScene(this.renderScene.Player.$.id, this.renderScene);
        });
    }
}